import { Box, Typography, Button, Grid, Paper, Divider } from '@mui/material';
import ReceiptLongOutlinedIcon from '@mui/icons-material/ReceiptLongOutlined';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ShoppingBagOutlinedIcon from '@mui/icons-material/ShoppingBagOutlined';
import LocalOfferOutlinedIcon from '@mui/icons-material/LocalOfferOutlined';

const SeccionResumenTotal = ({
  subTotal, onBack, shipping = {}, formatToCurrency,
  handleBuyCart, descuento, descuentoAplicado,
  totalGeneral, totalFinal, loading, isComplete = false,
}) => {
  const parseMonedaLatam = (valor) => {
    if (!valor) return 0;
    if (typeof valor === 'number') return valor;
    return parseFloat(valor.replace(/\./g, '').replace(',', '.'));
  };

  const subTotalNumber = parseMonedaLatam(totalFinal);
  const shippingNumber = shipping && !isNaN(Number(shipping.precioTotal)) ? Number(shipping.precioTotal) : 0;
  const total = subTotalNumber + shippingNumber;

  const format = (valor) =>
    formatToCurrency ? formatToCurrency(valor) : valor.toLocaleString('es-AR', { style: 'currency', currency: 'ARS', minimumFractionDigits: 2 });

  return (
    <Box sx={{ mb: 2 }}>
      <Paper elevation={0} sx={{
        border: '1px solid var(--border)', borderRadius: 'var(--radius-lg)',
        backgroundColor: 'var(--bg-card)', overflow: 'hidden',
      }}>
        {/* ── Header (solo en paso completo) ── */}
        {isComplete && (
          <Box sx={{ p: 2, borderBottom: '1px solid var(--border)', display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Box sx={{ width: 36, height: 36, borderRadius: 'var(--radius-md)', backgroundColor: 'var(--success-bg)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <ReceiptLongOutlinedIcon sx={{ color: 'var(--success)', fontSize: 20 }} />
            </Box>
            <Typography sx={{ fontFamily: 'var(--font-heading)', fontWeight: 800, fontSize: '1.05rem', color: 'var(--text-primary)' }}>
              Resumen del Pedido
            </Typography>
          </Box>
        )}

        <Box sx={{ p: { xs: 2, sm: 3 } }}>
          {/* ── Desglose (solo en paso completo) ── */}
          {isComplete && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, mb: 2 }}>
              {/* Subtotal */}
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography sx={{ fontFamily: 'var(--font-body)', fontSize: '0.88rem', color: 'var(--text-secondary)' }}>
                  Subtotal
                </Typography>
                <Typography sx={{
                  fontFamily: 'var(--font-mono)', fontSize: '0.9rem', fontWeight: 600,
                  color: descuentoAplicado > 0 ? 'var(--text-muted)' : 'var(--text-primary)',
                  textDecoration: descuentoAplicado > 0 ? 'line-through' : 'none',
                }}>
                  ${format(totalGeneral)}
                </Typography>
              </Box>

              {/* Descuento */}
              {descuentoAplicado > 0 && (
                <Box sx={{
                  display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                  p: 1.5, borderRadius: 'var(--radius-md)', backgroundColor: 'var(--success-bg)',
                  border: '1px solid rgba(16,185,129,0.15)',
                }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <LocalOfferOutlinedIcon sx={{ fontSize: 16, color: 'var(--success)' }} />
                    <Typography sx={{ fontFamily: 'var(--font-body)', fontSize: '0.82rem', color: 'var(--success)' }}>
                      Descuento <Typography component="span" sx={{ fontWeight: 700, fontFamily: 'var(--font-mono)' }}>{format(descuento.porcentaje)}%</Typography>
                    </Typography>
                  </Box>
                  <Typography sx={{ fontFamily: 'var(--font-mono)', fontSize: '0.88rem', fontWeight: 700, color: 'var(--success)' }}>
                    - ${format(descuentoAplicado)}
                  </Typography>
                </Box>
              )}
            </Box>
          )}

          {/* ── Total ── */}
          <Box sx={{
            borderTop: isComplete ? '2px solid var(--border)' : 'none',
            pt: isComplete ? 2 : 0,
            display: 'flex', justifyContent: 'space-between', alignItems: 'center',
          }}>
            <Typography sx={{ fontFamily: 'var(--font-heading)', fontWeight: 800, fontSize: '1.1rem', color: 'var(--text-primary)' }}>
              Total
            </Typography>
            <Typography sx={{
              fontFamily: 'var(--font-heading)', fontWeight: 800, fontSize: '1.4rem',
              color: 'var(--accent)', letterSpacing: '-0.02em',
            }}>
              ${format(total)}
            </Typography>
          </Box>

          {/* ── Garantías (solo en paso completo) ── */}
          {isComplete && (
            <Box sx={{ display: 'flex', gap: 2, mt: 2, flexWrap: 'wrap', justifyContent: 'center' }}>
              {[
                { emoji: '🔒', text: 'Compra segura' },
                { emoji: '🛡️', text: 'Datos protegidos' },
                { emoji: '💬', text: 'Soporte 24/7' },
              ].map((g) => (
                <Box key={g.text} sx={{
                  display: 'flex', alignItems: 'center', gap: 0.5,
                  px: 1.5, py: 0.5, borderRadius: 'var(--radius-full)',
                  backgroundColor: 'var(--bg-subtle)', border: '1px solid var(--border)',
                }}>
                  <Typography sx={{ fontSize: '0.75rem' }}>{g.emoji}</Typography>
                  <Typography sx={{ fontFamily: 'var(--font-body)', fontSize: '0.7rem', color: 'var(--text-muted)', fontWeight: 600 }}>
                    {g.text}
                  </Typography>
                </Box>
              ))}
            </Box>
          )}
        </Box>
      </Paper>

      {/* ── Botones (solo en paso completo) ── */}
      {isComplete && (
        <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 1.5, mt: 2.5 }}>
          <Button onClick={onBack} variant="outlined" fullWidth startIcon={<ArrowBackIcon sx={{ fontSize: 18 }} />}
            sx={{
              borderRadius: 'var(--radius-md)', fontFamily: 'var(--font-body)', fontWeight: 700,
              fontSize: '0.9rem', textTransform: 'none', py: 1.25,
              color: 'var(--text-secondary)', borderColor: 'var(--border)', borderWidth: '1.5px',
              '&:hover': { backgroundColor: 'var(--bg-subtle)', borderColor: 'var(--border-hover)' },
            }}>
            Volver
          </Button>
          <Button onClick={handleBuyCart} disabled={loading} variant="contained" fullWidth
            startIcon={<ShoppingBagOutlinedIcon sx={{ fontSize: 18 }} />}
            sx={{
              borderRadius: 'var(--radius-md)', fontFamily: 'var(--font-heading)', fontWeight: 700,
              fontSize: '0.95rem', textTransform: 'none', py: 1.25,
              backgroundColor: 'var(--success)',
              boxShadow: '0 4px 16px rgba(16,185,129,0.3)',
              '&:hover': { backgroundColor: '#059669', transform: 'translateY(-1px)', boxShadow: '0 6px 20px rgba(16,185,129,0.4)' },
              '&:active': { transform: 'scale(0.98)' },
              '&:disabled': { backgroundColor: 'var(--border)', boxShadow: 'none' },
            }}>
            {loading ? 'Procesando...' : 'Confirmar Compra'}
          </Button>
        </Box>
      )}
    </Box>
  );
};

export default SeccionResumenTotal;